<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_BUSINESS_AUTOMATION')"
            :label="$t('Add')"
            size="md"
            class="mt-0"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="otherBusinessRef"
      :items="otherBusinessList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Account Type -->
      <template #cell(accountType)="data">
        {{ data.value }}
      </template>

      <!-- Column: Account -->
      <template #cell(account)="data">
        <p class="mb-0">
          {{ data.value }}
        </p>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <b-dropdown
          variant="link"
          toggle-class="p-0"
          no-caret
          :right="$store.state.appConfig.isRTL"
        >

          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>
          <b-dropdown-item
            v-if="getUserPermission('MODIFY_BUSINESS_AUTOMATION')"
            @click="onSelectOtherBusinessItem(data.item, $event.target, 'edit')"
          >
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
          </b-dropdown-item>

          <b-dropdown-item @click="onSelectOtherBusinessItem(data.item, $event.target, 'view')">
            <feather-icon icon="EyeIcon" />
            <span class="align-middle ml-50"> {{ $t('View') }} </span>
          </b-dropdown-item>

          <b-dropdown-item @click="onDeleteOtherBusinessAutomation(data.item, $event.target)">
            <feather-icon icon="TrashIcon" />
            <span class="align-middle ml-50"> {{ $t('Delete') }} </span>
          </b-dropdown-item>
        </b-dropdown>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      no-close-on-backdrop
      :ok-title="$t('Save')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      :ok-disabled="isLoading"
      size="md"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="otherBusinessRef">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Account Type -->
            <b-col md="12">
              <SelectField
                id="account-type"
                v-model="otherBusinessFormData.accountType"
                :label="$t('Account Type')"
                :reduce="option => option.value"
                :options="accountTypeOptions"
                :error-name="$t('Account Type')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
                :set-selected="getGLAccount"
              />
            </b-col>
            <!-- Account Type -->

            <!-- Account -->
            <b-col md="12">
              <SelectField
                id="account"
                v-model="otherBusinessFormData.accountNumber"
                :label="$t('Account')"
                :options="accountOptions"
                :reduce="option => option.value"
                :error-name="$t('Account')"
                rules="required"
                item="label"
                :readonly="isReadonly"
                :placeholder="$t('Select')"
                :loading="isLoadingAccountNumber"
              />
            </b-col>
            <!-- Account -->
          </b-row>

        </b-form>
      </validation-observer>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="md"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchOtherBusinessAccount"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <b-col md="12">
          <SelectField
            id="account-type"
            v-model="filter.accountType"
            :label="$t('Account Type')"
            :reduce="option => option.value"
            :options="accountTypeOptions"
            :error-name="$t('Account Type')"
            rules="required"
            item="label"
            :placeholder="$t('Select')"
            :set-selected="getGLAccount"
          />
        </b-col>
        <!-- Account Type -->

        <!-- Account -->
        <b-col md="12">
          <SelectField
            id="account"
            v-model="filter.accountNumber"
            :label="$t('Account')"
            :options="accountOptions"
            :reduce="option => option.value"
            :error-name="$t('Account')"
            rules="required"
            item="label"
            :placeholder="$t('Select')"
          />
        </b-col>
        <!-- Account -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable,
  BDropdown, BDropdownItem, BModal, BSpinner,
  BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { toastAlert } from '@core/mixins/toast'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import SelectField from '../../../forms/SelectField.vue'
import addSpaceToString from '../../../../helpers/addSpaceToString'
import removeSpaceToString from '../../../../helpers/removeSpaceToString'
import Button from '../../../components/button/Button.vue'
import { operationPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    TableEntries,
    BSpinner,
    BTable,
    BDropdown,
    BDropdownItem,
    BModal,
    BForm,
    ValidationObserver,
    SelectField,
    TablePagination,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, toastAlert, permissionMixin],
  data() {
    return {
      sortBy: 'accountType',
      required,
      modalTitle: this.$t('Create Other Business Automation'),
      sortDesc: false,
      isLoadingAccountNumber: false,
      addSpaceToString,
      removeSpaceToString,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountType', sortable: true, label: 'Account Type' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'GL Code' },
        { key: 'actions', label: 'Actions' },
      ],
      accountTypeOptions: [],
      accountOptions: [],
      otherBusinessFormData: {
        accountType: '',
        accountNumber: '',
      },
      filter: {
        accountType: null,
        accountNumber: null,
      },
      otherBusinessList: [],
      showSpinner: false,
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchOtherBusinessAccount()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchOtherBusinessAccount()
    },
  },
  mounted() {
    this.fetchAccountTypes()
    this.fetchOtherBusinessAccount()
  },
  methods: {
    ...mapActions({
      getOtherBusinessAccount: 'operations/getOtherBusinessAccount',
      createOtherBusinessAccount: 'operations/createOtherBusinessAccountAction',
      updateOtherBusinessAccount: 'operations/updateOtherBusinessAccountAction',
      getGLAccountsByAccountType: 'account/getGLAccountsByList',
      getAccountTypes: 'account/getAccountTypesList',
      deleteOtherBusinessAutomation: 'operations/deleteBusinessAutomation',
    }),
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.otherBusinessRef.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const thisBusiness = this.$t('this Other Business?')
          this.confirm(this.setOtherBusinessAccount, { text: `${areYouSure} ${this.buttonLabel} ${thisBusiness}` })
        }
      })
    },

    async setOtherBusinessAccount() {
      const yourRequest = this.$t('Your request to')
      try {
        this.isLoading = true
        const otherBusinessFormData = {
          ...this.otherBusinessFormData,
          accountType: removeSpaceToString(this.otherBusinessFormData.accountType),
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createOtherBusinessAccount(this.otherBusinessFormData)
          : await this.updateOtherBusinessAccount(otherBusinessFormData)

        const { status } = response
        if (status === 200) {
          this.isLoading = false
          this.fetchOtherBusinessAccount()
          this.hideModal('modal-lg')
          const otherText = this.$t('Other Business was successful')
          this.alertSuccess({ text: `${yourRequest} ${this.buttonLabel} ${otherText}` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.hideModal('modal-lg')
          const failedText = this.$t('Other Business failed. Reason:')
          this.alertError({ text: `${yourRequest} ${this.buttonLabel} ${failedText} ${responseError.errorMessage}` })
        })
      } finally {
        this.isLoading = false
      }
    },

    async getGLAccount(selectedValue) {
      try {
        this.isLoadingAccountNumber = true
        this.accountOptions = []
        const response = await this.getGLAccountsByAccountType({ id: selectedValue, permission: operationPermissions.VIEW_BUSINESS_AUTOMATION })
        const { data, status } = await response
        if (status === 200) {
          this.accountOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.code.toString() }))
        }
      } catch (error) {
        // this.handleError(error)
        this.toastAlert({ title: error })
      } finally {
        this.isLoadingAccountNumber = false
      }
    },
    async fetchAccountTypes() {
      try {
        const response = await this.getAccountTypes()
        const { data } = await response
        this.accountTypeOptions = data.data.map(item => ({ label: item.description, value: item.index.toString() }))
      } catch (error) {
        this.handleError(error)
      }
    },
    async onDeleteOtherBusinessAutomation(item) {
      const deleteOtherBusinessAutomation = async () => {
        try {
          this.showSpinner = true
          const response = await this.deleteOtherBusinessAutomation({ routeName: 'OtherBusinessAutomation', id: item.id })
          const { status } = response
          if (status === 200) {
            this.fetchOtherBusinessAccount()
            this.alertSuccess({ text: this.$t('Deleted successfully') })
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(deleteOtherBusinessAutomation, { text: this.$t('Are you sure you want to delete this Other Business Automation?') })
    },
    async fetchOtherBusinessAccount() {
      try {
        this.isBusy = true
        const response = await this.getOtherBusinessAccount({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200) {
          this.otherBusinessList = data.data?.map(items => ({
            ...items,
            accountType: addSpaceToString(items.accountType),
          }))
          this.totalCount = data.recordCount
        } else {
          this.otherBusinessList = []
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetModal() {
      this.otherBusinessFormData = { ...null }
      this.isLoading = false
      this.isReadonly = false
      this.buttonLabel = this.$t('Save')
      this.modalTitle = this.$t('Create Other Business Automation')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchOtherBusinessAccount()
    },
    async onSelectOtherBusinessItem(items, activity, val) {
      try {
        this.showSpinner = true
        await this.fetchAccountTypes()
        const accountTypeCode = this.accountTypeOptions.find(item => item.value === items.accountTypeValue)
        this.getGLAccount(accountTypeCode.value)
        this.otherBusinessFormData = { ...items }
        this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
        this.modalTitle = val === 'edit' ? this.$t('Edit Other Business Automation') : this.$t('Other Business Automation')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
